import { List, Table, Typography, DatePicker, Button, Row, Col, Space } from "antd";
import Card from "antd/lib/card/Card";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { cglFormatDate, formatNumberWithCommas } from "utils/functions";
import "./index.css";
import { getData } from "apiServices/common";
import ListTable from "common/ListTable";
import { getPresetRanges } from "common/SearchForm/DateRangePresets";

export default function ProfitLossAccount() {
  const [data, setData] = useState({
    listOfBasicInfo: [],
    dataSource: [],
    isLoading: false,
  });

  const [dateRange, setDateRange] = useState([moment().startOf("month"), moment().endOf("month")]);
  // ux2(final-2): recompute "today" when the picker opens, not just at render (whole-branch
  // review M-2) — a tab left open across midnight otherwise offers a stale "Today"/"Yesterday".
  const [presetRanges, setPresetRanges] = useState(getPresetRanges);
  const [fetchError, setFetchError] = useState(null);

  // Fix round 1: guard against setState after unmount (a slow response should
  // not update state on a component the user has already navigated away from).
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      fixed: "left",
      render: (date) => cglFormatDate(date),
    },
    {
      title: "Estimated Daily Over Time",
      dataIndex: "estimated_over_time",
      key: "estimated_over_time",
      align: "right",
    },
    {
      title: "Total Cost + OT/Day",
      dataIndex: "expense_per_day",
      key: "expense_per_day",
      align: "right",
      render: (text) => formatNumberWithCommas(text),
    },
    {
      title: "Sewing Production (pcs)",
      dataIndex: "sewing_qty",
      key: "sewing_qty",
      align: "right",
    },
    {
      title: "Folding Production (pcs)",
      dataIndex: "folding_qty_pc",
      key: "folding_qty_pc",
      align: "right",
    },
    {
      title: "Folding Production (Dzn)",
      dataIndex: "folding_qty_dzn",
      key: "folding_qty_dzn",
      align: "right",
    },
    {
      title: "$ CM / Pcs",
      dataIndex: "avg_cm",
      key: "avg_cm",
      align: "right",
      render: (text) => formatNumberWithCommas(text),
    },
    {
      title: "Total CM / Day (USD)",
      dataIndex: "total_cm_usd",
      key: "total_cm_usd",
      align: "right",
      fixed: "right",
      render: (text) => formatNumberWithCommas(text),
    },
    {
      title: "Total CM / Day (Taka)",
      dataIndex: "total_cm_taka",
      key: "total_cm_taka",
      align: "right",
      fixed: "right",
      render: (text) => formatNumberWithCommas(text),
    },
    {
      title: "Operating Profit/Loss (Taka)",
      dataIndex: "operating_profit_loss",
      key: "operating_profit_loss",
      align: "right",
      fixed: "right",
      render: (text) => formatNumberWithCommas(text),
    },
    {
      title: () => `Cumulative Net Profit in ${dateRange[0].format("MMMM YYYY")} (Taka)`,
      dataIndex: "cumulative_net_profit",
      key: "cumulative_net_profit",
      align: "right",
      fixed: "right",
      render: (text) => formatNumberWithCommas(text),
    },
  ];

  const getListOfBasicInfo = (responseData) => {
    const basicInfo = [
      {
        name: "Name of Month",
        value: dateRange[0].format("MMMM YYYY"),
      },
      {
        name: "Total Days During the Month",
        value: responseData?.daysInMonth || 0,
      },
      {
        name: "Total Days of Leave During the Month",
        value: responseData?.leave_days || 0,
      },
      {
        name: "Total Working Days During the Month",
        value: responseData?.working_day || 0,
      },
    ];
    return basicInfo;
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    }
  };

  const resetDateRange = () => {
    const currentMonth = [moment().startOf("month"), moment().endOf("month")];
    setDateRange(currentMonth);
  };

  const handleSearch = () => {
    fetchPnlReportData(dateRange[0], dateRange[1]);
  };

  // Calculate cumulative net profit for each row
  const calculateCumulativeNetProfit = (data) => {
    let cumulativeSum = 0;
    return data.map((item) => {
      cumulativeSum += parseFloat(item.operating_profit_loss || 0);
      return {
        ...item,
        cumulative_net_profit: cumulativeSum.toFixed(2),
      };
    });
  };

  const fetchPnlReportData = async (startDate, endDate) => {
    setData((prevData) => ({
      ...prevData,
      isLoading: true,
    }));
    setFetchError(null);

    try {
      const formattedStartDate = startDate.format("YYYY-MM-DD");
      const formattedEndDate = endDate.format("YYYY-MM-DD");
      const url = `/api/pnl-report/daily-profit-loss-account?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;

      const response = await getData(url);

      if (!mountedRef.current) return;

      if (response?.data?.code === 200) {
        const responseData = response?.data?.data;

        // Process the data to add cumulative net profit
        const processedData = calculateCumulativeNetProfit(
          responseData?.dailyProfitLossAccount.map((item, index) => ({
            ...item,
            key: index.toString(),
          })),
        );

        setData({
          listOfBasicInfo: getListOfBasicInfo(responseData),
          dataSource: processedData,
          isLoading: false,
        });
      } else {
        setData((prevData) => ({
          ...prevData,
          isLoading: false,
        }));
        console.log("Error in API response");
        setFetchError("Could not load this list.");
      }
    } catch (error) {
      console.log("Error fetching Daily Profit & Loss Account data", error);
      if (!mountedRef.current) return;
      setData((prevData) => ({
        ...prevData,
        isLoading: false,
      }));
      setFetchError("Could not load this list.");
    }
  };

  useEffect(() => {
    fetchPnlReportData(dateRange[0], dateRange[1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card
      title={
        <>
          <Typography.Title level={3} style={{ textAlign: "center", marginBottom: 0 }}>
            CONCORDE GARMENTS LTD.
          </Typography.Title>
          <Typography.Title level={4} style={{ textAlign: "center", marginTop: 0 }}>
            DAILY PROFIT & LOSS ACCOUNT
          </Typography.Title>
        </>
      }
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={18}>
          <Space>
            <DatePicker.RangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              allowClear={false}
              ranges={presetRanges}
              onOpenChange={(open) => open && setPresetRanges(getPresetRanges())}
            />
            <Button type="primary" onClick={handleSearch}>
              Search
            </Button>
            <Button onClick={resetDateRange}>Reset</Button>
          </Space>
        </Col>
      </Row>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <List
          style={{
            width: "35%",
          }}
          bordered
          dataSource={data.listOfBasicInfo}
          renderItem={(item) => (
            <List.Item style={{ margin: "2px 4px" }}>
              <Typography.Text strong>{item?.name}:</Typography.Text>
              <Typography.Text>{item?.value}</Typography.Text>
            </List.Item>
          )}
        />
      </div>
      <div style={{ marginTop: 20, width: "100%" }}>
        <ListTable
          bordered
          className="ProfitLossAccount"
          columns={columns}
          dataSource={data.dataSource}
          scroll={{ x: "max-content", y: 500 }}
          pagination={false}
          loading={data.isLoading}
          error={fetchError}
          onRetry={() => fetchPnlReportData(dateRange[0], dateRange[1])}
          locale={{
            emptyText: `No records between ${dateRange[0].format("DD-MMM-YYYY")} and ${dateRange[1].format(
              "DD-MMM-YYYY",
            )}. Widen the range above.`,
          }}
          summary={(pageData) => {
            let totals = {};
            columns.forEach((col) => {
              if (col.dataIndex !== "date") {
                totals[col.dataIndex] = pageData
                  .reduce((sum, record) => sum + parseFloat(record[col.dataIndex] || 0), 0)
                  .toFixed(2);
              }
            });
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell>Total</Table.Summary.Cell>
                {columns.slice(1).map((col) => (
                  <Table.Summary.Cell key={col.dataIndex} align="right">
                    {formatNumberWithCommas(totals[col.dataIndex])}
                  </Table.Summary.Cell>
                ))}
              </Table.Summary.Row>
            );
          }}
        />
      </div>
    </Card>
  );
}
